import { ACTIVATION_EXPENSE_RATIO, ACTIVATION_SALES_DAYS, ACTIVE_COSTING_DISHES, activeCostingStatus, checkResultActivation } from './activation';
import { catalogCompleteness } from './catalogCompleteness';
import { unitsSoldFor } from './costing';
import { LABOR_CATEGORIES, MATERIAL_CATEGORIES } from './finance';
import { normalizeIngredientName } from './recipeProposal';
import { nombreMes } from './ventasPorPlato';
import type { ItemSubido } from './subirTodo';
import { SHARED_LOCATION_ID, effectiveLocationId, type DailyRevenue, type ExpenseCategory, type ExpenseEntry, type Ingredient, type MenuItem, type Recipe } from './types';

/*
 * «Después de lo que me has pasado, me faltaría esto y esto» (Pol, 29 oct
 * 2026). Una función pura que mira lo GUARDADO y lo LEÍDO sin guardar y dice,
 * en frases del dueño, qué falta para los dos hitos de activación
 * (shared/src/activation.ts), con un botón por hueco y una barra en dos tramos.
 *
 * El 30 % de la activación nunca se le enseña: se traduce en lo que suele
 * faltar (nóminas, alquiler, luz) para que sepa QUÉ subir.
 */

export type AccionFalta =
  | 'subir'
  | 'conectar-tpv'
  | 'importar-ventas'
  | 'importar-unidades'
  | 'importar-gastos'
  | 'receta'
  | 'ver-resultados';

export interface Falta {
  id: string;
  hito: 1 | 2;
  texto: string;
  detalle?: string;
  accion: { tipo: AccionFalta; etiqueta: string };
  /** Accion alternativa (p. ej. «Subir Excel» junto a «Conectar TPV»). */
  otra?: { tipo: AccionFalta; etiqueta: string };
  /** Nombres de platos o productos afectados (máx. 10), para el botón directo. */
  afectados?: string[];
}

export interface ProgresoSubirTodo {
  /** 0..1 · tramo «Tus números» (hito 1). */
  numeros: number;
  /** 0..1 · tramo «Tus costes» (hito 2). */
  costes: number;
  /** 0..1 · 70 % números + 30 % costes. */
  total: number;
}

export interface FaltasSubirTodo {
  hito1: {
    cumplido: boolean;
    /** Mes y local con el que mejor se va. */
    mes?: string;
    locationId?: string;
    diasVenta: number;
    ratioGastos: number;
  };
  hito2: { cumplido: boolean; platosEnCarta: number; platosConReceta: number; platosCosteados: number; conUnidades: boolean };
  faltas: Falta[];
  /** Con lo guardado y lo leído (rayado en la barra). */
  progreso: ProgresoSubirTodo;
  /** Solo con lo guardado (lleno en la barra). */
  progresoGuardado: ProgresoSubirTodo;
}

export interface EntradaFaltas {
  revenues: DailyRevenue[];
  expenses: ExpenseEntry[];
  menuItems: MenuItem[];
  recipes: Recipe[];
  ingredients: Ingredient[];
  /** Items del onboarding (se usan los `propuesto`; los `guardado` ya están en las listas de arriba). */
  items?: ItemSubido[];
  tpvConectado?: boolean;
  now?: Date;
}

const PESO = { ventas: 35, gastos: 35, carta: 5, recetas: 10, precios: 10, unidades: 5 } as const;


function finDeMes(mes: string): string {
  const [y, m] = mes.split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}

const ALQUILER_Y_SUMINISTROS: ExpenseCategory[] = ['alquiler', 'suministros', 'suministros_agua', 'suministros_electricidad', 'suministros_gas'];

/** Lo que cuenta como gasto en la P&L: ni por confirmar ni «no es gasto». */
const gastoQueCuenta = (e: ExpenseEntry) => !e.porConfirmar && !e.noEsGasto && e.netAmount > 0;

/** Una factura leída y no guardada, como si lo estuviera: solo lo que mira la activación. */
function gastoDeItem(item: ItemSubido): ExpenseEntry | undefined {
  if (item.kind !== 'gasto' || item.estado !== 'propuesto') return undefined;
  const p = item.propuesta;
  return {
    tenantId: '', entryId: `onb-${item.itemId}`, paymentType: p.paymentType, date: p.date, concept: p.concept,
    category: p.category, nature: p.nature, netAmount: p.netAmount, vatPct: p.vatPct, paid: p.paid,
    invoiceStatus: p.invoiceStatus, documentType: item.documentType, locationId: item.locationId,
  } as ExpenseEntry;
}

interface Ventana {
  mes: string;
  locationId: string;
  diasVenta: number;
  ratio: number;
  revenue: number;
  facturas: ExpenseEntry[];
  albaranes: ExpenseEntry[];
  met: boolean;
  puntos: number;
}

/** Puntos del tramo «Tus números» de una ventana, de 0 a 70. */
const puntosNumeros = (diasVenta: number, ratio: number) =>
  PESO.ventas * Math.min(1, diasVenta / ACTIVATION_SALES_DAYS) + PESO.gastos * Math.min(1, ratio / ACTIVATION_EXPENSE_RATIO);

/**
 * Hito 1 por mes y local (la P&L se mira por mes). Se queda con la ventana
 * con más puntos; a igualdad, la más reciente. Los gastos comunes («grupo»)
 * cuentan en todos los locales: es una aproximación, la de verdad la hace el
 * reparto al ver resultados.
 */
function mejorVentana(revenues: DailyRevenue[], expenses: ExpenseEntry[]): Ventana | undefined {
  const claves = new Set<string>();
  for (const r of revenues) claves.add(`${r.date.slice(0, 7)}|${effectiveLocationId(r.locationId)}`);
  for (const e of expenses) {
    const loc = effectiveLocationId(e.locationId);
    if (loc !== SHARED_LOCATION_ID) claves.add(`${e.date.slice(0, 7)}|${loc}`);
  }
  let mejor: Ventana | undefined;
  for (const clave of [...claves].sort()) {
    const [mes, locationId] = clave.split('|');
    const period = { start: `${mes}-01`, end: finDeMes(mes) };
    const delLocal = (loc: string | undefined) => {
      const l = effectiveLocationId(loc);
      return l === locationId || l === SHARED_LOCATION_ID;
    };
    const ventas = revenues.filter((r) => effectiveLocationId(r.locationId) === locationId);
    const gastos = expenses.filter((e) => delLocal(e.locationId) && e.date >= period.start && e.date <= period.end);
    const check = checkResultActivation({ revenues: ventas, expenses: gastos, period });
    const ventana: Ventana = {
      mes, locationId, diasVenta: check.salesDays, ratio: check.expenseRatio, revenue: check.revenue,
      facturas: gastos.filter((e) => (e.documentType ?? 'factura') === 'factura'),
      albaranes: gastos.filter((e) => e.documentType === 'albaran'),
      met: check.met,
      puntos: puntosNumeros(check.salesDays, check.expenseRatio),
    };
    if (!mejor || ventana.puntos >= mejor.puntos) mejor = ventana;
  }
  return mejor;
}

const listaHumana = (partes: string[]) => partes.length <= 1 ? partes.join('') : `${partes.slice(0, -1).join(', ')} ni ${partes[partes.length - 1]}`;

function faltasHito1(revenues: DailyRevenue[], expenses: ExpenseEntry[], ventana: Ventana | undefined, tpvConectado: boolean): Falta[] {
  const faltas: Falta[] = [];
  const facturas = expenses.filter((e) => (e.documentType ?? 'factura') === 'factura');
  const conVentas = revenues.length > 0 && (ventana?.revenue ?? 0) > 0;

  if (!conVentas) {
    faltas.push({
      id: 'ventas', hito: 1, texto: 'Me faltan tus ventas',
      detalle: 'Con una semana seguida me basta para enseñarte tus números.',
      accion: tpvConectado ? { tipo: 'importar-ventas', etiqueta: 'Subir Excel de ventas' } : { tipo: 'conectar-tpv', etiqueta: 'Conectar mi TPV' },
      ...(tpvConectado ? {} : { otra: { tipo: 'importar-ventas' as const, etiqueta: 'Subir Excel de ventas' } }),
    });
  } else if (ventana && ventana.diasVenta < ACTIVATION_SALES_DAYS) {
    const faltan = ACTIVATION_SALES_DAYS - ventana.diasVenta;
    faltas.push({
      id: 'ventas-dias', hito: 1,
      texto: `Tengo ${ventana.diasVenta} ${ventana.diasVenta === 1 ? 'día' : 'días'} seguidos de ventas de ${nombreMes(ventana.mes)}: me ${faltan === 1 ? 'falta 1 día' : `faltan ${faltan} días`} para una semana`,
      accion: { tipo: 'importar-ventas', etiqueta: 'Subir más ventas' },
    });
  }

  if (facturas.length === 0) {
    const albaranes = expenses.filter((e) => e.documentType === 'albaran');
    faltas.push({
      id: 'facturas', hito: 1,
      texto: albaranes.length ? `Solo tengo ${albaranes.length} ${albaranes.length === 1 ? 'albarán' : 'albaranes'}: para tus números necesito las facturas` : 'Me faltan tus facturas de gastos',
      detalle: albaranes.length ? 'El albarán dice lo que llegó; la factura es lo que pagas. Pide a tus proveedores la factura del mes.' : 'Fotos o PDFs de las facturas del mes, o el Excel que te pasa la gestoría.',
      accion: { tipo: 'subir', etiqueta: 'Subir facturas' },
      otra: { tipo: 'importar-gastos', etiqueta: 'Excel de la gestoría' },
    });
    return faltas;
  }

  if (!ventana || !conVentas) return faltas;
  if (ventana.facturas.length === 0) {
    const meses = [...new Set(facturas.map((e) => e.date.slice(0, 7)))].sort().map(nombreMes);
    faltas.push({
      id: 'mismo-mes', hito: 1,
      texto: `Tus ventas son de ${nombreMes(ventana.mes)} y tus facturas de ${listaHumana(meses).replace(/ ni /, ' y ')}`,
      detalle: `Para ver un mes necesito sus ventas y sus facturas. Sube las facturas de ${nombreMes(ventana.mes)}.`,
      accion: { tipo: 'subir', etiqueta: `Subir facturas de ${nombreMes(ventana.mes)}` },
    });
    return faltas;
  }

  if (ventana.ratio < ACTIVATION_EXPENSE_RATIO) {
    const categorias = new Set(ventana.facturas.map((e) => e.category));
    const ausentes: string[] = [];
    if (!MATERIAL_CATEGORIES.some((c) => categorias.has(c))) ausentes.push('compras de comida y bebida');
    if (!LABOR_CATEGORIES.some((c) => categorias.has(c))) ausentes.push('nóminas');
    if (!ALQUILER_Y_SUMINISTROS.some((c) => categorias.has(c))) ausentes.push('alquiler o luz');
    const mes = nombreMes(ventana.mes);
    if (ventana.albaranes.length) {
      faltas.push({
        id: 'albaranes', hito: 1,
        texto: `Tienes ${ventana.albaranes.length} ${ventana.albaranes.length === 1 ? 'albarán' : 'albaranes'} de ${mes}: para tus números necesito sus facturas`,
        accion: { tipo: 'subir', etiqueta: 'Subir facturas' },
      });
    }
    faltas.push({
      id: 'gastos-incompletos', hito: 1,
      texto: ausentes.length ? `No veo ${listaHumana(ausentes)} de ${mes}` : `Me faltan facturas de ${mes}`,
      detalle: `Con ${ventana.facturas.length} ${ventana.facturas.length === 1 ? 'factura' : 'facturas'} tus gastos se quedan cortos para un mes de ventas: el resultado saldría mejor de lo que es.`,
      accion: { tipo: 'subir', etiqueta: ausentes.includes('nóminas') ? 'Subir nóminas y facturas' : 'Subir más facturas' },
      otra: { tipo: 'importar-gastos', etiqueta: 'Excel de la gestoría' },
    });
  }
  return faltas;
}

interface Carta {
  nombre: string;
  recipeId?: string;
  conReceta: boolean;
  conUnidades: boolean;
}

function faltasHito2(entrada: EntradaFaltas, carta: Carta[], sinPrecio: number): Falta[] {
  const faltas: Falta[] = [];
  if (carta.length === 0) {
    faltas.push({ id: 'carta', hito: 2, texto: 'Me falta tu carta', detalle: 'Una foto de la carta me vale: saco los platos y sus precios.', accion: { tipo: 'subir', etiqueta: 'Subir la carta' } });
    return faltas;
  }
  const sinReceta = carta.filter((p) => !p.conReceta);
  if (sinReceta.length && carta.length - sinReceta.length < ACTIVE_COSTING_DISHES) {
    faltas.push({
      id: 'recetas', hito: 2,
      texto: `${sinReceta.length} ${sinReceta.length === 1 ? 'plato no tiene' : 'platos no tienen'} receta`,
      detalle: 'Empieza por tus 5 platos que más vendes: cuéntale a Alexia qué llevan o sube tus escandallos.',
      accion: { tipo: 'receta', etiqueta: 'Contar una receta' },
      otra: { tipo: 'subir', etiqueta: 'Subir escandallos' },
      afectados: sinReceta.slice(0, 10).map((p) => p.nombre),
    });
  }
  if (sinPrecio > 0) {
    faltas.push({
      id: 'precios', hito: 2,
      texto: `${sinPrecio} ${sinPrecio === 1 ? 'producto de tus recetas no tiene' : 'productos de tus recetas no tienen'} precio`,
      detalle: 'Los saco de las facturas de tus proveedores.',
      accion: { tipo: 'subir', etiqueta: 'Subir facturas de proveedores' },
    });
  }
  if (!carta.some((p) => p.conUnidades)) {
    faltas.push({
      id: 'unidades', hito: 2, texto: 'No sé cuántos vendes de cada plato',
      detalle: 'Está en el informe de ventas por producto de tu TPV.',
      accion: entrada.tpvConectado ? { tipo: 'importar-unidades', etiqueta: 'Subir ventas por plato' } : { tipo: 'conectar-tpv', etiqueta: 'Conectar mi TPV' },
      ...(entrada.tpvConectado ? {} : { otra: { tipo: 'importar-unidades' as const, etiqueta: 'Subir ventas por plato' } }),
    });
  }
  return faltas;
}

function cartaDe(menuItems: MenuItem[], recipes: Recipe[], items: ItemSubido[]): Carta[] {
  const recetas = new Map(recipes.map((r) => [r.recipeId, r]));
  const carta: Carta[] = menuItems.filter((m) => m.active).map((m) => ({
    nombre: m.name, recipeId: m.recipeId,
    conReceta: (recetas.get(m.recipeId)?.components.length ?? 0) > 0,
    conUnidades: unitsSoldFor(m) > 0 || Object.values(m.unitsSoldByLocation ?? {}).some((u) => u > 0),
  }));
  const yaEnCarta = new Set(carta.map((p) => normalizeIngredientName(p.nombre)));
  for (const item of items) {
    if (item.kind !== 'plato' || item.estado !== 'propuesto' || item.yaEnCarta) continue;
    const clave = normalizeIngredientName(item.nombre.valor);
    if (yaEnCarta.has(clave)) continue;
    yaEnCarta.add(clave);
    carta.push({ nombre: item.nombre.valor, conReceta: false, conUnidades: false });
  }
  return carta;
}

function progresoDe(ventana: Ventana | undefined, carta: Carta[], costeados: number): ProgresoSubirTodo {
  const numeros = ventana ? puntosNumeros(ventana.diasVenta, ventana.ratio) / (PESO.ventas + PESO.gastos) : 0;
  const conReceta = carta.filter((p) => p.conReceta).length;
  const puntosCostes = (carta.length > 0 ? PESO.carta : 0)
    + PESO.recetas * Math.min(1, conReceta / ACTIVE_COSTING_DISHES)
    + PESO.precios * Math.min(1, costeados / ACTIVE_COSTING_DISHES)
    + (carta.some((p) => p.conUnidades) ? PESO.unidades : 0);
  const costes = puntosCostes / (PESO.carta + PESO.recetas + PESO.precios + PESO.unidades);
  const redondo = (n: number) => Math.round(n * 1000) / 1000;
  return { numeros: redondo(numeros), costes: redondo(costes), total: redondo(numeros * 0.7 + costes * 0.3) };
}

export function calcularFaltas(entrada: EntradaFaltas): FaltasSubirTodo {
  const items = entrada.items ?? [];
  const guardados = entrada.expenses.filter(gastoQueCuenta);
  const leidos = items.map(gastoDeItem).filter((e): e is ExpenseEntry => Boolean(e));
  const gastos = [...guardados, ...leidos];

  const ventana = mejorVentana(entrada.revenues, gastos);
  const ventanaGuardada = mejorVentana(entrada.revenues, guardados);

  const completitud = catalogCompleteness(entrada.recipes, entrada.ingredients, entrada.menuItems);
  const sinPrecio = completitud.ingredientsUsed - completitud.ingredientsPriced;
  const carta = cartaDe(entrada.menuItems, entrada.recipes, items);
  const cartaGuardada = cartaDe(entrada.menuItems, entrada.recipes, []);

  const hito1Cumplido = Boolean(ventana?.met);
  const faltas = [
    ...(hito1Cumplido ? [] : faltasHito1(entrada.revenues, gastos, ventana, Boolean(entrada.tpvConectado))),
    ...faltasHito2(entrada, carta, sinPrecio),
  ];
  const conUnidades = carta.some((p) => p.conUnidades);
  return {
    hito1: {
      cumplido: hito1Cumplido,
      ...(ventana ? { mes: ventana.mes, locationId: ventana.locationId } : {}),
      diasVenta: ventana?.diasVenta ?? 0,
      ratioGastos: Math.round((ventana?.ratio ?? 0) * 1000) / 1000,
    },
    hito2: {
      cumplido: activeCostingStatus({ ingredients: entrada.ingredients, recipes: entrada.recipes, menuItems: entrada.menuItems, now: entrada.now }).active,
      platosEnCarta: carta.length,
      platosConReceta: carta.filter((p) => p.conReceta).length,
      platosCosteados: completitud.dishesCosted,
      conUnidades,
    },
    faltas,
    progreso: progresoDe(ventana, carta, completitud.dishesCosted),
    progresoGuardado: progresoDe(ventanaGuardada, cartaGuardada, completitud.dishesCosted),
  };
}

/** Lo que devuelve `GET /v1/subir-todo/jobs/:jobId`: la pantalla entera sale de aquí. */
export interface EstadoSubirTodo {
  job: import('./subirTodo').TrabajoSubirTodo;
  archivos: import('./subirTodo').ArchivoSubido[];
  items: Array<ItemSubido & { dudas: string[] }>;
  paginas: { usadas: number; limite: number };
  faltas: FaltasSubirTodo;
}

export interface CambiosItemSubido {
  descartar?: boolean;
  recuperar?: boolean;
  confirmar?: boolean;
  locationId?: string;
  gasto?: { concept?: string; supplierName?: string; invoiceNumber?: string; date?: string; netAmount?: number; vatPct?: number; category?: ExpenseCategory; documentType?: 'factura' | 'albaran'; noEsRepetida?: boolean };
  plato?: { nombre?: string; precio?: number; seccion?: string };
}

export interface ResultadoGuardarSubirTodo {
  guardados: number;
  conDudas: number;
  errores: Array<{ itemId: string; error: string }>;
  estado: EstadoSubirTodo;
}
